import React, { useEffect, useState, useRef } from 'react'
import { ArrowLeft, ArrowRight, Check, Upload, FileText, Camera, Shield, AlertCircle, Loader2, RefreshCcw, X, ScanLine } from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../config/api'

const ALL_steps = [
  { id: 'documents',    label: 'Documents',    icon: FileText },
  { id: 'biometric',    label: 'Biometric',    icon: Camera },
  { id: 'verification', label: 'Verification', icon: Shield },
  { id: 'review',       label: 'Review',       icon: Check },
]

/** Steps shown to the operator. Biometric is removed entirely when the venue's
 *  kyc.desktop_biometric_mode policy is 'hidden'; for 'required' and 'optional'
 *  it stays in the list (optional surfaces a Skip button on the step). */
function visibleSteps(biometricMode) {
  if (biometricMode === 'hidden') {
    return ALL_steps.filter((s) => s.id !== 'biometric')
  }
  return ALL_steps
}

const PROFILE_TYPES = [
  { 
    value: 'MEMBER', 
    label: 'New Member', 
    description: 'Fresh profile creation for a new club member',
    icon: '👤'
  },
  { 
    value: 'EXISTING_MEMBER', 
    label: 'Existing Member Migration', 
    description: 'Onboard an existing member to PaySafeGO profile',
    icon: '🔄'
  },
  { 
    value: 'VISITOR', 
    label: 'Visitor Profile', 
    description: 'Create profile for visitor (payout or flagged)',
    icon: '🎫'
  }
]

const DOCUMENT_TYPES = [
  { value: 'DRIVERS_LICENSE', label: "Driver's License" },
  { value: 'PASSPORT', label: 'Passport' },
  { value: 'MEDICARE', label: 'Medicare Card' },
  { value: 'PROOF_OF_AGE', label: 'Proof of Age Card' }
]

/** Strip huge base64 image blobs from ProfileSafe `raw` for JSON preview (avoids freezing the UI). */
function sanitizeProfileSafeRawForDisplay(raw) {
  if (raw == null || typeof raw !== 'object') return raw
  try {
    const clone = JSON.parse(JSON.stringify(raw))
    const od = clone.OutputData
    if (Array.isArray(od)) {
      for (const row of od) {
        const ocr = row?.OCRResponse
        if (ocr && typeof ocr.IDPhoto === 'string' && ocr.IDPhoto.length > 200) {
          ocr.IDPhoto = `[JPEG base64 omitted — ${ocr.IDPhoto.length} characters]`
        }
      }
    }
    return clone
  } catch {
    return raw
  }
}

/** Avoid displaying vendor-specific DVS integration codes to operators */
function formatDvsProviderForUi(provider) {
  if (provider == null || provider === '') return '—'
  const u = String(provider).toUpperCase()
  if (u === 'GREENID' || u === 'GREEN_ID') return 'DVS'
  return String(provider)
}

/**
 * Shared KYC onboarding wizard — patron (Profile Safe) or staff (Admin → Employees).
 * @param {'patron'|'staff'} variant
 * @param {string} [staffId] — required when variant=staff
 * @param {string} [employeeName]
 * @param {string} [employeeId]
 * @param {boolean} [embedded] — modal/drawer: hide page chrome, use callbacks
 * @param {() => void} [onComplete]
 * @param {() => void} [onClose]
 */
export default function KycOnboardingWizard({
  variant = 'patron',
  staffId,
  employeeName = '',
  employeeId = '',
  embedded = false,
  onComplete,
  onClose,
}) {
  const isStaff = variant === 'staff'
  const [currentStep, setCurrentStep] = useState(0)
  const [biometricMode, setBiometricMode] = useState('required') // required | optional | hidden
  const [biometricSkipped, setBiometricSkipped] = useState(false)
  const [biometricSkipReason, setBiometricSkipReason] = useState('') // freeform note when skipped
  const [multiDocumentEnabled, setMultiDocumentEnabled] = useState(false)
  /** Extra ID front/back pairs beyond the primary ProfileSafe upload.
   *  Each: { id, front: { file, preview } | null, back: { file, preview } | null, ocrStatus }
   *  ocrStatus: 'pending' | 'running' | 'done' | 'failed' (per-doc) */
  const [additionalDocuments, setAdditionalDocuments] = useState([])
  const steps = visibleSteps(biometricMode)
  const [loading, setLoading] = useState(false)

  // Resolve the venue's kyc.* policy once on mount: biometric mode (drives
  // the Biometric step visibility + Skip button) and multi-document flag
  // (drives the "Add another ID" affordance in the Documents step).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venue-config/resolved?category=kyc'))
        if (!r.ok) return
        const j = await r.json()
        if (cancelled) return
        const kyc = j?.data?.kyc || {}
        const readVal = (k) => {
          const raw = kyc[k]
          return typeof raw === 'object' ? raw?.value : raw
        }
        const mode = readVal('desktop_biometric_mode')
        if (mode && ['required', 'optional', 'hidden'].includes(String(mode))) {
          setBiometricMode(String(mode))
        }
        const multi = readVal('multi_document_enabled')
        if (multi === true || multi === 'true' || multi === 1 || multi === '1') {
          setMultiDocumentEnabled(true)
        }
      } catch {/* fall back to defaults: required + single-doc */}
    })()
    return () => { cancelled = true }
  }, [])
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [loadingMemberData, setLoadingMemberData] = useState(false)
  
  // Form data
  const [formData, setFormData] = useState({
    profileType: 'MEMBER',
    // Personal info
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    email: '',
    phone: '',
    // Address
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: 'NSW',
    postalCode: '',
    country: 'AU',
    // Existing member info
    existingMemberId: '',
    existingAccountId: '',
    // Visitor info
    visitorSource: '', // 'PAYOUT' or 'FLAGGED'
    visitorSystemRef: '',
    // Documents
    documents: [],
    // Biometric
    idFaceprint: null,
    verifiedFaceprint: null,
    // Verification flags
    runDVS: true,
    runAMLPEP: true,
    /** Primary ID type for DVS mapping (after ProfileSafe OCR) */
    primaryIdDocType: 'DRIVERS_LICENSE',
    /** Licence card number for AU DVS (GBG-required; OCR may read from front or back) */
    licenceCardNumber: ''
  })

  /** ProfileSafe: ID front (required for OCR), back (optional), same as PaySafeMobile RGBPage1/RGBPage2 */
  const [profileSafeIdFront, setProfileSafeIdFront] = useState(null)
  const [profileSafeIdBack, setProfileSafeIdBack] = useState(null)
  const [profileSafeOcrLoading, setProfileSafeOcrLoading] = useState(false)
  const [profileSafeOcrError, setProfileSafeOcrError] = useState(null)
  const [profileSafeRawEnvelope, setProfileSafeRawEnvelope] = useState(null)
  const [dvsVerifyLoading, setDvsVerifyLoading] = useState(false)
  const [dvsVerifyResult, setDvsVerifyResult] = useState(null)
  const [dvsVerifyError, setDvsVerifyError] = useState(null)

  const profileSafeFrontRef = useRef(null)
  const profileSafeBackRef = useRef(null)
  const verifiedFaceprintRef = useRef(null)

  const [profileSafePatron, setProfileSafePatron] = useState(null)
  /** User explicitly confirms OCR preview before Submit KYC (DVS). Reset on each new Extract. */
  const [profileSafeOcrConfirmed, setProfileSafeOcrConfirmed] = useState(false)

  const persistStaffWizardState = async (step, extra = {}) => {
    if (!isStaff || !staffId) return
    try {
      await authFetch(getLocalApiUrl(`/api/staff/${staffId}/kyc-onboarding-state`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentStep: step,
          wizardState: {
            formData: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              middleName: formData.middleName,
              dateOfBirth: formData.dateOfBirth,
              gender: formData.gender,
              email: formData.email,
              phone: formData.phone,
              addressLine1: formData.addressLine1,
              addressLine2: formData.addressLine2,
              city: formData.city,
              state: formData.state,
              postalCode: formData.postalCode,
              country: formData.country,
              primaryIdDocType: formData.primaryIdDocType,
              licenceCardNumber: formData.licenceCardNumber,
              runDVS: formData.runDVS,
              runAMLPEP: formData.runAMLPEP,
              profileType: 'STAFF',
            },
            biometricSkipped,
            biometricSkipReason,
            profileSafeOcrConfirmed,
            profileSafePatron,
            ...extra,
          },
        }),
      })
    } catch {/* non-fatal */}
  }

  // Staff: restore saved wizard step + form fields (files must be re-uploaded).
  useEffect(() => {
    if (!isStaff || !staffId) return
    let cancelled = false
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl(`/api/staff/${staffId}/kyc-onboarding-state`))
        const j = await r.json()
        if (cancelled || !r.ok || !j.success || !j.data) return
        const ws = j.data.wizard_state || {}
        if (Number.isFinite(Number(j.data.current_step))) {
          setCurrentStep(Number(j.data.current_step))
        }
        if (ws.formData && typeof ws.formData === 'object') {
          setFormData((prev) => ({ ...prev, ...ws.formData, profileType: 'STAFF' }))
        }
        if (ws.biometricSkipped) setBiometricSkipped(true)
        if (ws.biometricSkipReason) setBiometricSkipReason(ws.biometricSkipReason)
        if (ws.profileSafeOcrConfirmed) setProfileSafeOcrConfirmed(true)
        if (ws.profileSafePatron) setProfileSafePatron(ws.profileSafePatron)
      } catch {/* non-fatal */}
    })()
    return () => { cancelled = true }
  }, [isStaff, staffId])

  // Prefill staff name from HR record when opening wizard
  useEffect(() => {
    if (!isStaff || !employeeName?.trim()) return
    const parts = employeeName.trim().split(/\s+/)
    setFormData((prev) => ({
      ...prev,
      profileType: 'STAFF',
      firstName: prev.firstName || parts[0] || '',
      lastName: prev.lastName || parts.slice(1).join(' ') || '',
    }))
  }, [isStaff, employeeName])

  const profileRefForKyc = () => {
    if (isStaff) {
      return employeeId?.trim() || (staffId ? `STAFF-${staffId}` : 'staff_onboarding_web')
    }
    return (
      formData.existingMemberId?.trim() ||
      formData.visitorSystemRef?.trim() ||
      'profile_onboarding_web'
    )
  }

  const normalizeOcrDobToInputDate = (raw) => {
    if (!raw || !String(raw).trim()) return ''
    const t = String(raw).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t
    const m = t.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/)
    if (m) {
      const months = {
        jan: 0,
        feb: 1,
        mar: 2,
        apr: 3,
        may: 4,
        jun: 5,
        jul: 6,
        aug: 7,
        sep: 8,
        oct: 9,
        nov: 10,
        dec: 11
      }
      const mon = months[m[2].slice(0, 3).toLowerCase()]
      if (mon == null) return ''
      const d = new Date(Number(m[3]), mon, Number(m[1]))
      if (Number.isNaN(d.getTime())) return ''
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${mm}-${dd}`
    }
    return ''
  }

  const applyPatronToForm = (p) => {
    if (!p) return
    setFormData((prev) => {
      const next = {
        ...prev,
        firstName: p.firstName || prev.firstName,
        lastName: p.lastName || prev.lastName
      }
      const addr = (p.addressLine || '').trim()
      if (addr) {
        const comma = addr.indexOf(',')
        if (comma > 0) {
          next.addressLine1 = addr.slice(0, comma).trim()
          next.addressLine2 = addr.slice(comma + 1).trim()
        } else {
          next.addressLine1 = addr
        }
      }
      const iso = normalizeOcrDobToInputDate(p.dateOfBirthRaw)
      if (iso) next.dateOfBirth = iso
      if (p.state) next.state = p.state
      if (p.documentCardNumber) next.licenceCardNumber = p.documentCardNumber
      const expiryIso = normalizeOcrDobToInputDate(p.expiryDateRaw)
      if (expiryIso) next.documentExpiry = expiryIso
      // Map ProfileSafe's documentType string to our enum so the Detected chip
      // (and the submit payload) reflects what OCR saw. Staff can still
      // override via the Documents-step dropdown.
      if (p.documentType) {
        const dt = String(p.documentType).toLowerCase()
        if (/licen|driver/.test(dt))         next.primaryIdDocType = 'DRIVERS_LICENSE'
        else if (/passport/.test(dt))        next.primaryIdDocType = 'PASSPORT'
        else if (/medicare/.test(dt))        next.primaryIdDocType = 'MEDICARE'
        else if (/proof.?of.?age|poa/.test(dt)) next.primaryIdDocType = 'PROOF_OF_AGE'
      }
      return next
    })
  }

  const runProfileSafeOcr = async () => {
    setProfileSafeOcrLoading(true)
    setProfileSafeOcrError(null)
    setProfileSafeOcrConfirmed(false)
    try {
      if (!profileSafeIdFront?.file) {
        setProfileSafeOcrError('Upload ID front for ProfileSafe (Documents step).')
        return
      }
      // Selfie required unless the venue's biometric policy says skip is OK
      // AND the operator pressed Skip on the Biometric step.
      const allowSkip = biometricSkipped || biometricMode === 'hidden'
      if (!allowSkip && !formData.verifiedFaceprint?.file) {
        setProfileSafeOcrError('Add a live selfie under Verified Photo (Biometric step) first.')
        return
      }
      const fd = new FormData()
      fd.append('idFront', profileSafeIdFront.file)
      if (profileSafeIdBack?.file) fd.append('idBack', profileSafeIdBack.file)
      if (!allowSkip && formData.verifiedFaceprint?.file) {
        fd.append('selfie', formData.verifiedFaceprint.file)
      }
      // When skipped, the operator's optional reason rides along on the multipart
      // for the backend audit row (biometric_skip_reason column).
      if (biometricSkipped && biometricSkipReason.trim()) {
        fd.append('skip_reason', biometricSkipReason.trim())
      }
      const res = await authFetch(getLocalApiUrl('/api/profiles/profilesafe/data-capture'), {
        method: 'POST',
        body: fd
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || json.message || 'ProfileSafe OCR failed')
      const p = json.data?.patron
      if (json.data?.raw) setProfileSafeRawEnvelope(json.data.raw)
      setProfileSafePatron(p || null)
      if (p) applyPatronToForm(p)
      setDvsVerifyResult(null)
      setDvsVerifyError(null)

      // Multi-document follow-on: OCR each additional ID front/back as
      // OCR-only (no selfie). Each call writes its own profile_safe_ocr_submissions
      // row for the audit trail. The primary doc above remains the canonical
      // patron source; additional docs are supplementary KYC evidence.
      if (multiDocumentEnabled && additionalDocuments.length) {
        for (let i = 0; i < additionalDocuments.length; i++) {
          const doc = additionalDocuments[i]
          if (!doc.front?.file) continue
          setAdditionalDocuments((arr) => {
            const next = [...arr]
            if (next[i]) next[i] = { ...next[i], ocrStatus: 'running' }
            return next
          })
          try {
            const fd2 = new FormData()
            fd2.append('idFront', doc.front.file)
            if (doc.back?.file) fd2.append('idBack', doc.back.file)
            // No selfie — backend takes the OCR-only branch.
            const r2 = await authFetch(getLocalApiUrl('/api/profiles/profilesafe/data-capture'), {
              method: 'POST',
              body: fd2,
            })
            const j2 = await r2.json().catch(() => ({}))
            const ok2 = r2.ok && j2.success
            setAdditionalDocuments((arr) => {
              const next = [...arr]
              if (next[i]) {
                next[i] = {
                  ...next[i],
                  ocrStatus: ok2 ? 'done' : 'failed',
                  ocrError: ok2 ? null : (j2.error || `HTTP ${r2.status}`),
                  ocrPatron: ok2 ? (j2.data?.patron || null) : null,
                }
              }
              return next
            })
          } catch (e) {
            setAdditionalDocuments((arr) => {
              const next = [...arr]
              if (next[i]) next[i] = { ...next[i], ocrStatus: 'failed', ocrError: e.message || 'failed' }
              return next
            })
          }
        }
      }
    } catch (e) {
      setProfileSafeOcrError(e.message || 'ProfileSafe request failed')
    } finally {
      setProfileSafeOcrLoading(false)
    }
  }

  const addAnotherDocument = () => {
    setAdditionalDocuments((arr) => [
      ...arr,
      { id: `addl-${Date.now()}-${arr.length}`, front: null, back: null, ocrStatus: 'pending' },
    ])
  }
  const removeAdditionalDocument = (id) => {
    setAdditionalDocuments((arr) => arr.filter((d) => d.id !== id))
  }
  const setAdditionalDocumentFile = (id, side, file) => {
    if (!file) return
    const preview = URL.createObjectURL(file)
    setAdditionalDocuments((arr) =>
      arr.map((d) => (d.id === id ? { ...d, [side]: { file, preview }, ocrStatus: 'pending' } : d)),
    )
  }

  const submitKycDvs = async () => {
    setDvsVerifyLoading(true)
    setDvsVerifyError(null)
    setDvsVerifyResult(null)
    try {
      const docNum = profileSafePatron?.documentNumber?.trim()
      if (!docNum) {
        setDvsVerifyError(
          'Licence number is required — run ProfileSafe OCR on the ID front so the licence number (document number) is extracted.'
        )
        return
      }
      if (!formData.firstName?.trim() || !formData.lastName?.trim()) {
        setDvsVerifyError('First and last name are required for DVS verification.')
        return
      }
      const isAuDl =
        (formData.primaryIdDocType === 'DRIVERS_LICENSE' || formData.primaryIdDocType === 'PROOF_OF_AGE') &&
        String(formData.country || 'AU').toUpperCase().startsWith('AU')
      const state = String(formData.state || 'NSW').trim().toUpperCase()
      const cardNum = String(
        formData.licenceCardNumber || profileSafePatron?.documentCardNumber || ''
      ).trim()
      if (isAuDl && !cardNum) {
        setDvsVerifyError(
          'Card number is required for Australian driver licence DVS (GBG greenID — separate from the licence number). ' +
            'Use OCR if it appears on the licence, or enter it manually. GBG test NSW: licence 11111111, card 1111111111.'
        )
        return
      }
      if (isAuDl && ['NSW', 'QLD', 'SA'].includes(state) && cardNum.replace(/\D/g, '').length !== 10) {
        setDvsVerifyError(
          'Card number must be exactly 10 digits for NSW/QLD/SA (not the licence number). GBG test: licence 11111111, card 1111111111.'
        )
        return
      }
      const body = {
        document_type_ui: formData.primaryIdDocType,
        document_number: docNum,
        document_card_number: cardNum || undefined,
        given_name: formData.firstName.trim(),
        family_name: formData.lastName.trim(),
        date_of_birth: formData.dateOfBirth || undefined,
        expiry_date: formData.documentExpiry || profileSafePatron?.expiryDateRaw || undefined,
        state_of_issue: formData.state || undefined,
        country_code: formData.country || 'AU',
        profile_ref: profileRefForKyc(),
        profile_type: isStaff ? 'STAFF' : (formData.profileType || 'MEMBER'),
        visitor_source: formData.visitorSource || undefined,
        document_count:
          (formData.documents?.length || 0) +
          (profileSafeIdFront ? 1 : 0) +
          (profileSafeIdBack ? 1 : 0) ||
          1,
        has_faceprint: !!(formData.verifiedFaceprint?.file || formData.idFaceprint?.file)
      }
      const res = await authFetch(getLocalApiUrl('/api/profiles/onboarding/kyc-dvs-verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || json.message || 'DVS verification failed')
      setDvsVerifyResult(json.data)
    } catch (e) {
      setDvsVerifyError(e.message || 'DVS request failed')
    } finally {
      setDvsVerifyLoading(false)
    }
  }

  const handleProfileSafeIdUpload = (e, side) => {
    const file = e.target.files?.[0]
    if (!file) return
    const preview = URL.createObjectURL(file)
    if (side === 'front') {
      if (profileSafeIdFront?.preview) URL.revokeObjectURL(profileSafeIdFront.preview)
      setProfileSafeIdFront({ file, preview })
    } else {
      if (profileSafeIdBack?.preview) URL.revokeObjectURL(profileSafeIdBack.preview)
      setProfileSafeIdBack({ file, preview })
    }
    e.target.value = ''
  }

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }

  const loadExistingMemberData = async () => {
    if (!formData.existingMemberId) {
      setError('Please enter an existing member ID first')
      return
    }

    try {
      setLoadingMemberData(true)
      setError(null)

      // Try to fetch from API
      try {
        const response = await authFetch(getLocalApiUrl(`/api/profiles/member/${formData.existingMemberId}`))
        const result = await response.json()

        if (response.ok && result.success && result.data) {
          const member = result.data
          
          // Pre-fill form with member data
          updateFormData({
            firstName: member.firstName || member.first_name || '',
            middleName: member.middleName || member.middle_name || '',
            lastName: member.lastName || member.last_name || '',
            dateOfBirth: member.dateOfBirth || member.date_of_birth || '',
            gender: member.gender || '',
            email: member.email || '',
            phone: member.phone || member.phone_mobile || '',
            addressLine1: member.addressLine1 || member.address_line1 || '',
            addressLine2: member.addressLine2 || member.address_line2 || '',
            city: member.city || '',
            state: member.state || 'NSW',
            postalCode: member.postalCode || member.postal_code || '',
            country: member.country || 'AU'
          })

          // If member has photos, load them
          if (member.idFaceprintUrl) {
            // Fetch and convert to blob for preview
            const imgResponse = await fetch(member.idFaceprintUrl)
            const blob = await imgResponse.blob()
            const file = new File([blob], 'id_faceprint.jpg', { type: blob.type })
            updateFormData({
              idFaceprint: {
                file: file,
                preview: URL.createObjectURL(blob)
              }
            })
          }

          if (member.verifiedFaceprintUrl) {
            const imgResponse = await fetch(member.verifiedFaceprintUrl)
            const blob = await imgResponse.blob()
            const file = new File([blob], 'verified_faceprint.jpg', { type: blob.type })
            updateFormData({
              verifiedFaceprint: {
                file: file,
                preview: URL.createObjectURL(blob)
              }
            })
          }

          // If member has documents, load them
          if (member.documents && member.documents.length > 0) {
            const docs = await Promise.all(
              member.documents.map(async (doc) => {
                const frontResponse = await fetch(doc.frontImageUrl)
                const frontBlob = await frontResponse.blob()
                const frontFile = new File([frontBlob], `${doc.documentType}_front.jpg`, { type: frontBlob.type })
                
                return {
                  id: Date.now() + Math.random(),
                  type: doc.documentType,
                  file: frontFile,
                  name: frontFile.name,
                  preview: URL.createObjectURL(frontBlob)
                }
              })
            )
            updateFormData({ documents: docs })
          }

          setLoadingMemberData(false)
          return
        }
      } catch (apiError) {
        console.log('API not available, using mock data:', apiError.message)
      }

      // Fallback: Mock data for demo
      const mockMemberData = {
        '990824': {
          firstName: 'Balasubramanian',
          middleName: '',
          lastName: 'KARUPPAIAH',
          dateOfBirth: '1996-06-05',
          gender: 'male',
          email: 'bala.k@example.com',
          phone: '+61 412 345 678',
          addressLine1: '123 Collins Street',
          addressLine2: '',
          city: 'Melbourne',
          state: 'VIC',
          postalCode: '3000',
          country: 'AU',
          hasPhotos: true
        },
        '50535': {
          firstName: 'Senthil',
          middleName: '',
          lastName: 'Ramachandran',
          dateOfBirth: '1985-03-20',
          gender: 'male',
          email: 'senthil.r@example.com',
          phone: '+61 423 456 789',
          addressLine1: '456 George Street',
          addressLine2: 'Apt 12',
          city: 'Sydney',
          state: 'NSW',
          postalCode: '2000',
          country: 'AU',
          hasPhotos: true
        }
      }

      const member = mockMemberData[formData.existingMemberId]

      if (!member) {
        setError(`Member # "${formData.existingMemberId}" not found in system. Please check the number and try again.`)
        setLoadingMemberData(false)
        return
      }

      // Pre-fill form with mock member data
      updateFormData({
        firstName: member.firstName,
        middleName: member.middleName,
        lastName: member.lastName,
        dateOfBirth: member.dateOfBirth,
        gender: member.gender,
        email: member.email,
        phone: member.phone,
        addressLine1: member.addressLine1,
        addressLine2: member.addressLine2,
        city: member.city,
        state: member.state,
        postalCode: member.postalCode,
        country: member.country
      })

      setLoadingMemberData(false)

    } catch (err) {
      console.error('Error loading member data:', err)
      setError(err.message)
      setLoadingMemberData(false)
    }
  }

  const handleNext = () => {
    setError(null)

    if (currentStep === 0) {
      const hasProfileSafeId = !!profileSafeIdFront?.file
      if (!hasProfileSafeId) {
        setError(
          'Upload an ID front under ProfileSafe OCR — the document type is detected automatically from the scan.'
        )
        return
      }
    }

    if (currentStep === 1 && steps[currentStep]?.id === 'biometric' && !biometricSkipped) {
      if (!formData.verifiedFaceprint?.file) {
        setError('Add a live selfie (Verified Photo) before continuing — required for ProfileSafe OCR.')
        return
      }
    }

    if (currentStep === 2) {
      if (!profileSafePatron?.documentNumber?.trim()) {
        setError('Run Extract ID (ProfileSafe OCR) and confirm the captured preview before continuing.')
        return
      }
      if (!profileSafeOcrConfirmed) {
        setError('Confirm the OCR preview (details match your ID) before continuing.')
        return
      }
    }

    if (currentStep === 3) {
      if (!formData.firstName?.trim() || !formData.lastName?.trim() || !formData.dateOfBirth) {
        setError('Please complete required identity fields (name and date of birth).')
        return
      }
      if (!isStaff && formData.profileType === 'EXISTING_MEMBER' && !formData.existingMemberId?.trim()) {
        setError('Please provide the existing member ID or change profile type.')
        return
      }
      if (!isStaff && formData.profileType === 'VISITOR' && !formData.visitorSource) {
        setError('Please specify visitor source or change profile type.')
        return
      }
    }

    if (currentStep < steps.length - 1) {
      const next = currentStep + 1
      setCurrentStep(next)
      if (isStaff) persistStaffWizardState(next)
    }
  }

  const handleBack = () => {
    setError(null)
    if (currentStep > 0) {
      const prev = currentStep - 1
      setCurrentStep(prev)
      if (isStaff) persistStaffWizardState(prev)
    }
  }

  const handleDocumentUpload = (e, docType) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    const newDocs = files.map(file => ({
      id: Date.now() + Math.random(),
      type: docType,
      file: file,
      name: file.name,
      preview: URL.createObjectURL(file)
    }))

    updateFormData({
      documents: [...formData.documents, ...newDocs]
    })
  }

  const removeDocument = (docId) => {
    const doc = formData.documents.find(d => d.id === docId)
    if (doc && doc.preview) {
      URL.revokeObjectURL(doc.preview)
    }
    updateFormData({
      documents: formData.documents.filter(d => d.id !== docId)
    })
  }

  const handleVerifiedFaceprintUpload = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const preview = URL.createObjectURL(file)
    updateFormData({ verifiedFaceprint: { file, preview } })
  }

  const handleSubmit = async () => {
    try {
      setLoading(true)
      setError(null)

      // Create FormData for multipart upload
      const formDataToSend = new FormData()
      
      // Add profile data
      formDataToSend.append('profileType', isStaff ? 'STAFF' : formData.profileType)
      formDataToSend.append('firstName', formData.firstName)
      formDataToSend.append('middleName', formData.middleName || '')
      formDataToSend.append('lastName', formData.lastName)
      formDataToSend.append('dateOfBirth', formData.dateOfBirth)
      formDataToSend.append('gender', formData.gender || '')
      formDataToSend.append('email', formData.email || '')
      formDataToSend.append('phone', formData.phone || '')
      formDataToSend.append('addressLine1', formData.addressLine1 || '')
      formDataToSend.append('addressLine2', formData.addressLine2 || '')
      formDataToSend.append('city', formData.city || '')
      formDataToSend.append('state', formData.state)
      formDataToSend.append('postalCode', formData.postalCode || '')
      formDataToSend.append('country', formData.country)
      
      // Add type-specific fields
      if (formData.profileType === 'EXISTING_MEMBER') {
        formDataToSend.append('existingMemberId', formData.existingMemberId)
        formDataToSend.append('existingAccountId', formData.existingAccountId || '')
      }
      
      if (formData.profileType === 'VISITOR') {
        formDataToSend.append('visitorSource', formData.visitorSource)
        formDataToSend.append('visitorSystemRef', formData.visitorSystemRef || '')
      }
      
      // Add verification flags
      formDataToSend.append('runDVS', formData.runDVS)
      formDataToSend.append('runAMLPEP', formData.runAMLPEP)
      
      // Add documents (typed uploads, then ProfileSafe ID images for blob/KYC storage — same files as OCR)
      let docIdx = 0
      formData.documents.forEach((doc) => {
        formDataToSend.append(`documents[${docIdx}]`, doc.file)
        formDataToSend.append(`documentTypes[${docIdx}]`, doc.type)
        docIdx += 1
      })
      const primaryType = formData.primaryIdDocType || 'DRIVERS_LICENSE'
      if (profileSafeIdFront?.file) {
        formDataToSend.append(`documents[${docIdx}]`, profileSafeIdFront.file)
        formDataToSend.append(`documentTypes[${docIdx}]`, primaryType)
        docIdx += 1
      }
      if (profileSafeIdBack?.file) {
        formDataToSend.append(`documents[${docIdx}]`, profileSafeIdBack.file)
        formDataToSend.append(`documentTypes[${docIdx}]`, `${primaryType}_BACK`)
      }
      
      // Add faceprints
      if (formData.idFaceprint) {
        formDataToSend.append('idFaceprint', formData.idFaceprint.file)
      }
      if (formData.verifiedFaceprint) {
        formDataToSend.append('verifiedFaceprint', formData.verifiedFaceprint.file)
      }

      const response = await authFetch(
        getLocalApiUrl(isStaff ? `/api/staff/${staffId}/kyc-onboard` : '/api/profiles/onboard'),
        {
          method: 'POST',
          body: formDataToSend,
        }
      )

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || (isStaff ? 'Failed to complete staff KYC' : 'Failed to create profile'))
      }

      setSuccess(true)

      if (embedded && onComplete) {
        setTimeout(() => onComplete(result.data), 1500)
      } else if (!isStaff) {
        setTimeout(() => {
          window.location.href = '/profile-safe/verification-queue'
        }, 2000)
      } else {
        setTimeout(() => {
          window.location.href = '/admin/employees/' + staffId
        }, 2000)
      }

    } catch (err) {
      console.error('Onboarding error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Documents — first step (upload ID before OCR)
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Identity Documents</h3>
            <p className="text-sm text-gray-600">
              Upload a clear photo of the <strong>ID front</strong> (and optionally the back).
              The document type — driver's licence, passport, Medicare card or proof of age —
              is detected automatically from the scan. Name, address and licence number are
              captured later on the <strong>Verification</strong> step.
            </p>

            {profileSafeIdFront && (
              <div className="border rounded-md p-3 bg-gray-50 flex items-center gap-3">
                <span className="text-xs font-medium text-gray-500">Detected type:</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                  {DOCUMENT_TYPES.find(d => d.value === formData.primaryIdDocType)?.label || 'Driver’s Licence'}
                </span>
                <span className="text-xs text-gray-500">·</span>
                <label className="text-xs text-gray-500 flex items-center gap-2">
                  Override:
                  <select
                    value={formData.primaryIdDocType}
                    onChange={(e) => setFormData(prev => ({ ...prev, primaryIdDocType: e.target.value }))}
                    className="text-xs border-gray-300 rounded px-2 py-0.5"
                  >
                    {DOCUMENT_TYPES.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            {profileSafeIdFront && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 flex items-center gap-2">
                <Check className="w-5 h-5 text-teal-600" />
                <span className="text-sm text-teal-800">
                  ProfileSafe ID{profileSafeIdBack ? ' (front + back)' : ' (front)'}
                </span>
              </div>
            )}

            <div className="border-2 border-dashed border-psg-teal/40 rounded-lg p-5 space-y-4 bg-teal-50/30">
              <div className="flex items-start gap-2">
                <ScanLine className="w-5 h-5 text-psg-teal flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-psg-teal-900">ProfileSafe OCR (PaySafeMobile parity)</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Upload a clear photo of the <strong>ID front</strong> and optionally the <strong>back</strong>. On the
                    Biometric step, add a <strong>live selfie</strong> (Verified Photo). Then use <strong>Verification</strong>{' '}
                    to run OCR against the ProfileSafe Azure API — extracted name, DOB, address, and licence number feed KYC
                    and document verification (DVS).
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ID front (required for ProfileSafe)</label>
                  <input
                    ref={profileSafeFrontRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleProfileSafeIdUpload(e, 'front')}
                  />
                  {profileSafeIdFront ? (
                    <div className="relative border rounded overflow-hidden">
                      <img src={profileSafeIdFront.preview} alt="ID front" className="w-full h-36 object-cover" />
                      <button
                        type="button"
                        onClick={() => {
                          URL.revokeObjectURL(profileSafeIdFront.preview)
                          setProfileSafeIdFront(null)
                        }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-7 h-7 text-sm"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => profileSafeFrontRef.current?.click()}
                      className="w-full h-28 border-2 border-dashed rounded flex items-center justify-center text-sm text-gray-600 hover:border-psg-teal"
                    >
                      Choose ID front
                    </button>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID back (optional)
                    <span className="block text-xs font-normal text-gray-500 mt-0.5">
                      Optional second image — helps OCR read the <strong>card number</strong> when it is not on the front.
                    </span>
                  </label>
                  <input
                    ref={profileSafeBackRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleProfileSafeIdUpload(e, 'back')}
                  />
                  {profileSafeIdBack ? (
                    <div className="relative border rounded overflow-hidden">
                      <img src={profileSafeIdBack.preview} alt="ID back" className="w-full h-36 object-cover" />
                      <button
                        type="button"
                        onClick={() => {
                          URL.revokeObjectURL(profileSafeIdBack.preview)
                          setProfileSafeIdBack(null)
                        }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-7 h-7 text-sm"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => profileSafeBackRef.current?.click()}
                      className="w-full h-28 border-2 border-dashed rounded flex items-center justify-center text-sm text-gray-600 hover:border-psg-teal"
                    >
                      Choose ID back
                    </button>
                  )}
                </div>
              </div>
            </div>

            {multiDocumentEnabled && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Additional identity documents</h4>
                    <p className="text-xs text-gray-500">
                      Optional. Add another ID (e.g. Medicare card alongside a driver's licence) if your tier
                      requires safe-harbour KYC. Each one is OCR'd independently; the primary above stays the
                      canonical name/DOB source.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addAnotherDocument}
                    className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-psg-teal text-psg-teal rounded-md hover:bg-teal-50"
                  >
                    + Add another ID
                  </button>
                </div>

                {additionalDocuments.map((doc, idx) => (
                  <div key={doc.id} className="border rounded-lg p-3 space-y-2 bg-white">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-medium text-gray-700">Document #{idx + 2}</div>
                      <div className="flex items-center gap-2">
                        {doc.ocrStatus === 'running' && (
                          <span className="text-[10px] text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">OCR running…</span>
                        )}
                        {doc.ocrStatus === 'done' && (
                          <span className="text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">OCR'd ✓</span>
                        )}
                        {doc.ocrStatus === 'failed' && (
                          <span className="text-[10px] text-red-700 bg-red-50 px-1.5 py-0.5 rounded" title={doc.ocrError || ''}>OCR failed</span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeAdditionalDocument(doc.id)}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block">
                        <div className="text-[10px] text-gray-500 mb-1">Front (required)</div>
                        <input
                          type="file"
                          accept="image/*"
                          className="text-xs"
                          onChange={(e) => setAdditionalDocumentFile(doc.id, 'front', e.target.files?.[0])}
                        />
                        {doc.front?.preview && <img src={doc.front.preview} alt="" className="mt-1 h-16 object-cover rounded" />}
                      </label>
                      <label className="block">
                        <div className="text-[10px] text-gray-500 mb-1">Back (optional)</div>
                        <input
                          type="file"
                          accept="image/*"
                          className="text-xs"
                          onChange={(e) => setAdditionalDocumentFile(doc.id, 'back', e.target.files?.[0])}
                        />
                        {doc.back?.preview && <img src={doc.back.preview} alt="" className="mt-1 h-16 object-cover rounded" />}
                      </label>
                    </div>
                    {doc.ocrStatus === 'done' && doc.ocrPatron && (
                      <div className="text-[10px] text-gray-600">
                        OCR result: {doc.ocrPatron.documentType || '—'} · {doc.ocrPatron.documentNumber || ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )

      case 1: // Biometric
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Biometric Verification (FacePrint)</h3>
            <p className="text-sm text-gray-600">
              Add a <strong>live selfie</strong> for biometric verification and for ProfileSafe OCR (face match against the
              portrait on your ID — that portrait comes from the <strong>ID front/back</strong> you upload on Documents,
              not a separate crop).
            </p>

            {biometricMode === 'optional' && (
              <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-amber-900">Biometric is optional for this venue</div>
                    <p className="text-amber-900 mt-0.5">
                      Press <strong>Skip biometric</strong> to enrol the patron without a selfie. ProfileSafe will run
                      OCR-only and the audit row will record <em>you</em> as the skipping operator (plus the optional reason
                      below).
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setBiometricSkipped(true)
                      setCurrentStep((i) => Math.min(i + 1, steps.length - 1))
                    }}
                    className="shrink-0 px-3 py-1.5 text-xs font-medium border border-amber-700 text-amber-900 rounded-md hover:bg-amber-100"
                  >
                    Skip biometric →
                  </button>
                </div>
                <label className="block text-xs text-amber-900">
                  Reason (optional, max 500 chars) — visible on the audit row only
                  <textarea
                    value={biometricSkipReason}
                    onChange={(e) => setBiometricSkipReason(e.target.value.slice(0, 500))}
                    placeholder="e.g. patron declined live capture; venue camera offline; existing patron returning"
                    className="mt-1 w-full text-xs border-amber-300 rounded p-1.5 bg-white"
                    rows={2}
                  />
                </label>
              </div>
            )}

            <div className="border rounded-lg p-4 max-w-lg">
              <h4 className="font-medium mb-3">Verified Photo (live capture)</h4>
              <input
                ref={verifiedFaceprintRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleVerifiedFaceprintUpload}
              />
              {formData.verifiedFaceprint ? (
                <div className="space-y-3">
                  <img
                    src={formData.verifiedFaceprint.preview}
                    alt="Verified Faceprint"
                    className="w-full h-64 object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      URL.revokeObjectURL(formData.verifiedFaceprint.preview)
                      updateFormData({ verifiedFaceprint: null })
                    }}
                    className="btn-secondary w-full"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => verifiedFaceprintRef.current?.click()}
                  className="w-full h-64 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center hover:border-psg-teal hover:bg-teal-50 transition-colors"
                >
                  <Camera className="w-12 h-12 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Upload Verified Photo</span>
                </button>
              )}
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-sm text-gray-800">
                <strong>Note:</strong> Recommended for security and required for ProfileSafe Extract ID. The ID document
                images you already uploaded supply the document-side portrait; ProfileSafe compares those to this live photo.
              </div>
            </div>
          </div>
        )

      case 2: // Verification
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Verification &amp; KYC</h3>
            <p className="text-sm text-gray-600">
              Run <strong>Extract ID</strong> to pull name, DOB, address, licence number, and card number (when visible on the scan) plus selfie.
              Upload front and back if the card number was not read from the front alone.
              Review and confirm the preview, then optionally <strong>Submit KYC</strong> for DVS and your configured AML/PEP
              checks. Choose any extra screening flags below.
            </p>

            <div className="space-y-4">
              <label className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.runDVS}
                  onChange={(e) => updateFormData({ runDVS: e.target.checked })}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium">Document Verification Service (DVS)</div>
                  <div className="text-sm text-gray-600">
                    Verify identity documents against government databases (Australian DVS).
                    Recommended for all member profiles.
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.runAMLPEP}
                  onChange={(e) => updateFormData({ runAMLPEP: e.target.checked })}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium">AML/PEP Screening</div>
                  <div className="text-sm text-gray-600">
                    Screen against sanctions lists, PEP databases, and adverse media.
                    Required for compliance with AUSTRAC regulations where applicable. Depending on configuration, AML/PEP may
                    run in the same workflow as DVS or via separate screening services (for example{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">/api/aml/checks/run</code> with{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">AML_SCREENING_PROVIDER</code>). The{' '}
                    <strong>Submit KYC</strong> action below queues document verification (DVS); full AML/PEP automation
                    follows your environment setup.
                  </div>
                </div>
              </label>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <strong>Important:</strong> Verification checks will run asynchronously. 
                  You can monitor their progress in the Verification Queue dashboard after submission.
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-5 space-y-4 bg-slate-50">
              <h4 className="font-semibold flex items-center gap-2">
                <ScanLine className="w-5 h-5 text-psg-teal" />
                ProfileSafe → DVS
              </h4>
              <p className="text-sm text-gray-600">
                Run <strong>Extract ID (ProfileSafe)</strong> after you have uploaded ID front (Documents) and a verified
                selfie (Biometric). Then use <strong>Submit KYC</strong> — this creates a{' '}
                <strong>verification_queue</strong> entry (DVS pending), runs your configured document-verification
                pipeline, and updates the{' '}
                <a href="/profile-safe/verification-queue" className="text-psg-teal underline font-medium">
                  Verification Queue
                </a>
                . Provider routing and sandbox behaviour are configured on the server (for example{' '}
                <code className="text-xs bg-white px-1 rounded">DVS_PROVIDER</code>, stub modes for non-production).
              </p>
              <p className="text-sm text-gray-700 bg-white border border-slate-200 rounded-lg px-3 py-2 mt-3">
                After extraction, <strong>review the captured fields</strong> below and confirm they match the ID. Only
                then use <strong>Submit KYC</strong> to send the same values for DVS processing. On the next step (
                <strong>Review</strong>) you can still edit name, address, email and phone before the final profile submit.
              </p>
              {profileSafeOcrError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded p-2">{profileSafeOcrError}</div>
              )}
              {dvsVerifyError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded p-2">{dvsVerifyError}</div>
              )}
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={runProfileSafeOcr}
                  disabled={profileSafeOcrLoading}
                  className="btn-primary disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {profileSafeOcrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
                  Extract ID (ProfileSafe OCR)
                </button>
              </div>

              {profileSafePatron && (
                <div className="border-2 border-teal-200 rounded-lg p-4 bg-white space-y-3 mt-4">
                  <div className="font-semibold text-teal-900 flex items-center gap-2">
                    <ScanLine className="w-5 h-5 text-psg-teal" />
                    Captured data (preview)
                  </div>
                  <p className="text-xs text-gray-600">
                    Values below are applied to the form (name, DOB, address). Confirm before submitting to DVS.
                  </p>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    {[
                      ['Given name', profileSafePatron.firstName],
                      ['Family name', profileSafePatron.lastName],
                      ['Date of birth', profileSafePatron.dateOfBirthRaw || formData.dateOfBirth || '—'],
                      ['Document type', profileSafePatron.documentType],
                      ['Licence number', profileSafePatron.documentNumber],
                      ['Card number (if read)', profileSafePatron.documentCardNumber],
                      ['State', profileSafePatron.state],
                      ['Address (as read)', profileSafePatron.addressLine],
                      ['ProfileSafe Profile ID', profileSafePatron.profileID]
                    ].map(([label, val]) => (
                      <div key={label} className="border-b border-slate-100 pb-1 sm:pb-0 sm:border-0">
                        <dt className="text-gray-500 text-xs">{label}</dt>
                        <dd className="font-medium text-gray-900 break-words">{val != null && val !== '' ? String(val) : '—'}</dd>
                      </div>
                    ))}
                  </dl>
                  {(formData.primaryIdDocType === 'DRIVERS_LICENSE' ||
                    formData.primaryIdDocType === 'PROOF_OF_AGE') &&
                    String(formData.country || 'AU').toUpperCase().startsWith('AU') && (
                    <div className="border-t border-slate-100 pt-3 space-y-2">
                      <p className="text-xs text-gray-600 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                        Australian DVS via GBG requires both <strong>licence number</strong> and <strong>card number</strong>{' '}
                        (separate values — do not copy the licence number here). OCR fills the card number when it appears on
                        the licence; otherwise enter it manually.
                      </p>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Card number <span className="text-red-600">*</span>
                        <span className="font-normal text-gray-500">
                          {profileSafePatron?.documentCardNumber
                            ? ' (from OCR — edit if wrong)'
                            : ' (required by GBG if not read by OCR)'}
                        </span>
                      </label>
                      <input
                        type="text"
                        className="input-field w-full max-w-md"
                        value={formData.licenceCardNumber}
                        onChange={(e) => updateFormData({ licenceCardNumber: e.target.value })}
                        placeholder={
                          ['NSW', 'QLD', 'SA'].includes(String(formData.state || 'NSW').toUpperCase())
                            ? 'GBG test: 1111111111 (licence 11111111)'
                            : 'As shown on licence'
                        }
                        autoComplete="off"
                      />
                    </div>
                  )}
                  {(() => {
                    const plain = profileSafeRawEnvelope?.OutputData?.[0]?.OCRResponse?.PlainDocument
                    if (!plain?.ExpirationDate && plain?.DocumentConfidenceScore == null) return null
                    return (
                      <div className="text-xs text-gray-600 flex flex-wrap gap-4 pt-1 border-t border-slate-100">
                        {plain?.ExpirationDate && (
                          <span>
                            <span className="text-gray-500">Expiry (OCR): </span>
                            {plain.ExpirationDate}
                          </span>
                        )}
                        {plain?.DocumentConfidenceScore != null && plain.DocumentConfidenceScore !== '' && (
                          <span>
                            <span className="text-gray-500">Document confidence: </span>
                            {typeof plain.DocumentConfidenceScore === 'number'
                              ? plain.DocumentConfidenceScore.toFixed(3)
                              : String(plain.DocumentConfidenceScore)}
                          </span>
                        )}
                      </div>
                    )
                  })()}
                  {profileSafeRawEnvelope && (
                    <details className="text-xs border border-slate-200 rounded-md bg-slate-50">
                      <summary className="cursor-pointer px-3 py-2 font-medium text-gray-700 select-none">
                        Raw ProfileSafe response (JSON, images stripped)
                      </summary>
                      <pre className="max-h-64 overflow-auto p-3 text-[11px] leading-snug border-t border-slate-200 bg-white whitespace-pre-wrap break-words">
                        {JSON.stringify(sanitizeProfileSafeRawForDisplay(profileSafeRawEnvelope), null, 2)}
                      </pre>
                    </details>
                  )}
                  {!profileSafeOcrConfirmed ? (
                    <button
                      type="button"
                      onClick={() => setProfileSafeOcrConfirmed(true)}
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Confirm — these details match the ID
                    </button>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3 text-sm text-teal-900 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
                      <Check className="w-5 h-5 text-teal-600 flex-shrink-0" />
                      <span>
                        Confirmed. You can submit KYC for DVS below.{' '}
                        <button
                          type="button"
                          className="text-psg-teal underline font-medium"
                          onClick={() => setProfileSafeOcrConfirmed(false)}
                        >
                          Undo confirmation
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-3 items-center pt-2">
                <button
                  type="button"
                  onClick={submitKycDvs}
                  disabled={
                    dvsVerifyLoading ||
                    !formData.runDVS ||
                    !profileSafePatron?.documentNumber?.trim() ||
                    !formData.firstName?.trim() ||
                    !formData.lastName?.trim() ||
                    !profileSafeOcrConfirmed
                  }
                  className="btn-secondary disabled:opacity-50 inline-flex items-center gap-2"
                  title={
                    !formData.runDVS
                      ? 'Enable DVS in the checkbox above'
                      : !profileSafePatron?.documentNumber?.trim()
                        ? 'Run Extract ID first — licence number (front) required'
                        : !profileSafeOcrConfirmed
                          ? 'Confirm the captured data preview above'
                          : ''
                  }
                >
                  {dvsVerifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                  Submit KYC (DVS)
                </button>
                {profileSafePatron && !profileSafeOcrConfirmed && (
                  <span className="text-sm text-amber-800">Confirm the OCR preview above to enable this button.</span>
                )}
              </div>
              {dvsVerifyResult?.dvs && (
                <div className="text-sm border border-teal-200 rounded-lg p-3 bg-white space-y-1">
                  <div className="font-medium text-teal-900">DVS result</div>
                  {dvsVerifyResult.queueDisplayId && (
                    <div className="text-xs text-gray-600">
                      Queue ID:{' '}
                      <span className="font-mono">{dvsVerifyResult.queueDisplayId}</span> — view in Verification Queue.
                    </div>
                  )}
                  <div>Verified: {dvsVerifyResult.dvs.verified ? 'yes' : 'no'} — {formatDvsProviderForUi(dvsVerifyResult.dvs.provider)}</div>
                  {dvsVerifyResult.dvs.is_stub != null && (
                    <div className="text-amber-800">Stub / mock: {String(dvsVerifyResult.dvs.is_stub)}</div>
                  )}
                  {dvsVerifyResult.providerInfo && (
                    <div className="text-xs text-gray-500">
                      Server-side DVS integration settings apply (managed by your administrator).
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )

      case 3: // Review
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Review & Submit</h3>
            <p className="text-sm text-gray-600">
              {isStaff ? (
                <>
                  Identity fields are filled from <strong>ProfileSafe OCR</strong> where possible — review and edit before
                  submitting. Add email and phone for HR contact records.
                </>
              ) : (
                <>
                  Identity fields are filled from <strong>ProfileSafe OCR</strong> where possible — review and edit before
                  submitting. Add email and phone for club contact. Profile type defaults to <strong>New Member</strong>.
                </>
              )}
            </p>

            <div className="space-y-4">
              {isStaff && (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <h4 className="font-semibold mb-2">Employee</h4>
                  <p className="text-sm text-gray-700">
                    <strong>{employeeName || `${formData.firstName} ${formData.lastName}`.trim()}</strong>
                    {employeeId ? (
                      <span className="text-gray-500"> · ID {employeeId}</span>
                    ) : null}
                  </p>
                </div>
              )}

              {!isStaff && (
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Profile Type</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {PROFILE_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => updateFormData({ profileType: type.value })}
                      className={`p-4 border-2 rounded-lg text-left transition-all hover:border-psg-teal ${
                        formData.profileType === type.value
                          ? 'border-psg-teal bg-psg-yellow/10'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="text-2xl mb-2">{type.icon}</div>
                      <div className="font-semibold text-sm">{type.label}</div>
                      <div className="text-xs text-gray-600 mt-1">{type.description}</div>
                    </button>
                  ))}
                </div>
              </div>
              )}

              {!isStaff && formData.profileType === 'EXISTING_MEMBER' && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <h4 className="font-semibold text-teal-900 mb-3">Existing Member Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Existing Member # *</label>
                      <input
                        type="text"
                        className="input-field w-full"
                        value={formData.existingMemberId}
                        onChange={(e) => updateFormData({ existingMemberId: e.target.value })}
                        placeholder="e.g., 990824"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Account ID (optional)</label>
                      <input
                        type="text"
                        className="input-field w-full"
                        value={formData.existingAccountId}
                        onChange={(e) => updateFormData({ existingAccountId: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={loadExistingMemberData}
                      disabled={!formData.existingMemberId || loadingMemberData}
                      className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingMemberData ? (
                        <>
                          <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <RefreshCcw className="w-4 h-4 inline mr-2" />
                          Load existing member data
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {!isStaff && formData.profileType === 'VISITOR' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-900 mb-3">Visitor Source</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Source *</label>
                      <select
                        className="select-field w-full"
                        value={formData.visitorSource}
                        onChange={(e) => updateFormData({ visitorSource: e.target.value })}
                      >
                        <option value="">Select...</option>
                        <option value="PAYOUT">Payout triggered</option>
                        <option value="FLAGGED">Physically flagged</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Visitor system reference</label>
                      <input
                        type="text"
                        className="input-field w-full"
                        value={formData.visitorSystemRef}
                        onChange={(e) => updateFormData({ visitorSystemRef: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="border rounded-lg p-4 space-y-4">
                <div>
                  <h4 className="font-semibold mb-1">Identity & contact</h4>
                  <p className="text-xs text-gray-500">
                    Typically prefilled from OCR — edit if anything does not match the physical ID.
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First name *</label>
                    <input
                      type="text"
                      className="input-field w-full"
                      value={formData.firstName}
                      onChange={(e) => updateFormData({ firstName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Middle name</label>
                    <input
                      type="text"
                      className="input-field w-full"
                      value={formData.middleName}
                      onChange={(e) => updateFormData({ middleName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last name *</label>
                    <input
                      type="text"
                      className="input-field w-full"
                      value={formData.lastName}
                      onChange={(e) => updateFormData({ lastName: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of birth *</label>
                    <input
                      type="date"
                      className="input-field w-full"
                      value={formData.dateOfBirth}
                      onChange={(e) => updateFormData({ dateOfBirth: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select
                      className="select-field w-full"
                      value={formData.gender}
                      onChange={(e) => updateFormData({ gender: e.target.value })}
                    >
                      <option value="">Select...</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="prefer_not_to_say">Prefer not to say</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      className="input-field w-full"
                      value={formData.email}
                      onChange={(e) => updateFormData({ email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      className="input-field w-full"
                      value={formData.phone}
                      onChange={(e) => updateFormData({ phone: e.target.value })}
                      placeholder="+61 4XX XXX XXX"
                    />
                  </div>
                </div>
                <h4 className="font-semibold pt-2">Address</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address line 1</label>
                    <input
                      type="text"
                      className="input-field w-full"
                      value={formData.addressLine1}
                      onChange={(e) => updateFormData({ addressLine1: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address line 2</label>
                    <input
                      type="text"
                      className="input-field w-full"
                      value={formData.addressLine2}
                      onChange={(e) => updateFormData({ addressLine2: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      className="input-field w-full"
                      value={formData.city}
                      onChange={(e) => updateFormData({ city: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <select
                      className="select-field w-full"
                      value={formData.state}
                      onChange={(e) => updateFormData({ state: e.target.value })}
                    >
                      <option value="NSW">NSW</option>
                      <option value="VIC">VIC</option>
                      <option value="QLD">QLD</option>
                      <option value="SA">SA</option>
                      <option value="WA">WA</option>
                      <option value="TAS">TAS</option>
                      <option value="NT">NT</option>
                      <option value="ACT">ACT</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal code</label>
                    <input
                      type="text"
                      className="input-field w-full"
                      value={formData.postalCode}
                      onChange={(e) => updateFormData({ postalCode: e.target.value })}
                      maxLength="4"
                    />
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Documents</h4>
                <div className="text-sm space-y-1">
                  <div>
                    Typed uploads: {formData.documents.length} document
                    {formData.documents.length !== 1 ? 's' : ''}
                  </div>
                  {profileSafeIdFront && (
                    <div className="text-gray-700">
                      ProfileSafe ID: front
                      {profileSafeIdBack ? ' + back' : ''}
                    </div>
                  )}
                </div>
              </div>

              {/* Biometrics */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Biometric Data</h4>
                <div className="text-sm space-y-1">
                  <div>
                    <span className="text-gray-600">Verified (live) photo:</span>{' '}
                    <span className="font-medium">{formData.verifiedFaceprint ? 'Uploaded' : 'Not provided'}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Document portrait for comparison comes from your uploaded ID front/back (Documents / ProfileSafe), not a
                    separate ID face crop.
                  </p>
                </div>
              </div>

              {/* Verification */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Verification Checks</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    {formData.runDVS ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <X className="w-4 h-4 text-gray-400" />
                    )}
                    <span>Document Verification Service (DVS)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {formData.runAMLPEP ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <X className="w-4 h-4 text-gray-400" />
                    )}
                    <span>AML/PEP Screening</span>
                  </div>
                  {profileSafePatron && (
                    <div className="mt-3 pt-3 border-t text-gray-700">
                      <div className="font-medium text-gray-900">ProfileSafe OCR</div>
                      <div className="mt-1">Name: {profileSafePatron.firstName} {profileSafePatron.lastName}</div>
                      {profileSafePatron.documentNumber && (
                        <div className="text-xs text-gray-500">Document # captured (masked in logs)</div>
                      )}
                    </div>
                  )}
                  {dvsVerifyResult?.dvs && (
                    <div className="mt-3 pt-3 border-t text-gray-700">
                      <div className="font-medium text-gray-900">DVS (verification queue)</div>
                      {dvsVerifyResult.queueDisplayId && (
                        <div className="text-xs font-mono text-gray-600">{dvsVerifyResult.queueDisplayId}</div>
                      )}
                      <div>
                        Verified: {dvsVerifyResult.dvs.verified ? 'yes' : 'no'} —{' '}
                        {formatDvsProviderForUi(dvsVerifyResult.dvs.provider)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  if (success) {
    return (
      <div className={embedded ? 'py-4' : 'max-w-4xl mx-auto py-8'}>
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-8 text-center">
          <div className="w-16 h-16 bg-psg-teal rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-semibold text-teal-900 mb-2">
            {isStaff ? 'Employee KYC Submitted!' : 'Profile Created Successfully!'}
          </h2>
          <p className="text-teal-800 mb-4">
            {isStaff
              ? 'Identity documents are on file and verification checks are queued.'
              : 'The profile has been created and verification checks are now queued.'}
          </p>
          {!embedded && (
            <p className="text-sm text-teal-700">
              {isStaff ? 'Returning to employee detail…' : 'Redirecting to Verification Queue...'}
            </p>
          )}
        </div>
      </div>
    )
  }

  const wizardTitle = isStaff ? 'Employee KYC onboarding' : 'Profile Onboarding'
  const wizardSubtitle =
    'Upload ID photos first, then Biometric selfie → Verification runs OCR to fill identity → Review adds contact details and submits.'

  return (
    <div className={embedded ? 'space-y-4' : 'max-w-6xl mx-auto space-y-6'}>
      {!embedded && (
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">{wizardTitle}</h1>
            <p className="text-sm text-gray-600 mt-1">{wizardSubtitle}</p>
          </div>
          <button
            onClick={() => (onClose ? onClose() : window.history.back())}
            className="btn-secondary"
          >
            <ArrowLeft className="w-4 h-4 inline mr-2" />
            Back
          </button>
        </div>
      </div>
      )}

      {embedded && (
        <div className="flex items-start justify-between gap-3 pb-2 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{wizardTitle}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{wizardSubtitle}</p>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              aria-label="Close wizard"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      )}

      {/* Progress Steps */}
      <div className={embedded ? '' : 'card'}>
        <div className={`flex items-center justify-between ${embedded ? 'px-1 py-2' : ''}`}>
          {steps.map((step, index) => {
            const Icon = step.icon
            const isActive = index === currentStep
            const isCompleted = index < currentStep
            
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                      isCompleted
                        ? 'bg-psg-teal text-white'
                        : isActive
                        ? 'bg-psg-yellow text-psg-teal-900'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isCompleted ? <Check className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                  </div>
                  <div className={`text-sm font-medium ${isActive ? 'text-psg-teal' : 'text-gray-600'}`}>
                    {step.label}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 ${isCompleted ? 'bg-psg-teal' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className={embedded ? '' : 'card'}>
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4 inline mr-2" />
            Back
          </button>

          {currentStep < steps.length - 1 ? (
            <button onClick={handleNext} className="btn-primary">
              Next
              <ArrowRight className="w-4 h-4 inline ml-2" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="btn-primary disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                  {isStaff ? 'Submitting KYC...' : 'Creating Profile...'}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 inline mr-2" />
                  {isStaff ? 'Submit employee KYC' : 'Submit Profile'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
